import OpenAI from 'openai';
import { JobDescription, InterviewConfig, InterviewFeedback, InterviewQuestion } from '../types';

// Create OpenAI client with API key validation
const createOpenAIClient = () => {
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  
  if (!apiKey) {
    throw new Error('OpenAI API key is not configured');
  }

  if (!apiKey.startsWith('sk-')) {
    throw new Error('Invalid OpenAI API key format');
  }

  return new OpenAI({
    apiKey,
    dangerouslyAllowBrowser: true
  });
};

const SARAH_INTRO = {
  en: "Hi, I'm Sarah, your AI interviewer today. I'll be asking you a series of questions that have been prepared for this role. Are you ready to begin?",
  es: "Hola, soy Sarah, tu entrevistadora de IA hoy. Te haré una serie de preguntas que han sido preparadas para este puesto. ¿Estás listo para empezar?"
};

// Validate interview configuration
const validateConfig = (config: InterviewConfig) => {
  if (!config.language || !['en', 'es'].includes(config.language)) {
    throw new Error('Invalid language configuration');
  }

  if (!config.tone || !['formal', 'casual', 'friendly'].includes(config.tone)) {
    throw new Error('Invalid tone configuration');
  }

  if (!config.technicalDepth || !['basic', 'intermediate', 'advanced'].includes(config.technicalDepth)) {
    throw new Error('Invalid technical depth configuration');
  }

  if (!config.duration || config.duration < 1) {
    throw new Error('Invalid duration configuration');
  }
};

// Validate job description
const validateJobDescription = (jobDescription: JobDescription) => {
  if (!jobDescription?.title?.trim()) {
    throw new Error('Job title is required');
  }

  if (!jobDescription?.description?.trim()) {
    throw new Error('Job description is required');
  }

  if (!Array.isArray(jobDescription?.requirements) || jobDescription.requirements.length === 0) {
    throw new Error('Job requirements are required');
  }

  if (!Array.isArray(jobDescription?.responsibilities) || jobDescription.responsibilities.length === 0) {
    throw new Error('Job responsibilities are required');
  }
};

export async function generateInterviewQuestions(
  jobDescription: JobDescription,
  config: InterviewConfig
): Promise<InterviewQuestion[]> {
  try {
    // Validate inputs
    validateJobDescription(jobDescription);
    validateConfig(config);

    const openai = createOpenAIClient();

    const prompt = `Generate ${config.duration} interview questions for a ${jobDescription.title} position.
      Job Description: ${jobDescription.description}
      Requirements: ${JSON.stringify(jobDescription.requirements)}
      Responsibilities: ${JSON.stringify(jobDescription.responsibilities)}
      
      Create questions that:
      - Are appropriate for ${config.technicalDepth} technical depth
      - Use ${config.tone} tone
      - Include a mix of technical, behavioral, and situational questions
      - Are specific to the role and requirements
      
      Return the questions in this exact JSON format:
      {
        "questions": [
          {
            "question": "question text",
            "type": "technical|behavioral|situational",
            "order": number
          }
        ]
      }
      
      Language: ${config.language === 'es' ? 'Spanish' : 'English'}`;

    const completion = await openai.chat.completions.create({
      model: config.model || "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "You are an expert at creating relevant interview questions. Always respond with valid JSON in the exact format specified."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: config.temperature || 0.7,
      max_tokens: config.maxTokens || 2000,
      response_format: { type: "json_object" }
    });

    const response = completion.choices[0]?.message?.content;
    if (!response) {
      throw new Error('No response received from OpenAI');
    }

    try {
      const parsed = JSON.parse(response);
      if (!Array.isArray(parsed.questions)) {
        throw new Error('Invalid response format');
      }

      return parsed.questions.map((q: any, index: number) => ({
        id: crypto.randomUUID(),
        question: q.question.trim(),
        type: q.type,
        order: index + 1,
        isCustom: false
      }));
    } catch (error) {
      console.error('Error parsing OpenAI response:', error);
      throw new Error('Failed to parse interview questions');
    }
  } catch (error) {
    console.error('Error generating questions:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to generate questions: ${error.message}`);
    }
    throw new Error('Failed to generate interview questions');
  }
}

export async function generateInterviewQuestion(
  jobDescription: JobDescription,
  config: InterviewConfig,
  previousQuestions: string[] = [],
  previousAnswers: string[] = []
): Promise<string> {
  try {
    // Validate inputs
    validateJobDescription(jobDescription);
    validateConfig(config);

    // If this is the initial greeting, return Sarah's intro
    if (config.isInitialGreeting) {
      return SARAH_INTRO[config.language === 'es' ? 'es' : 'en'];
    }

    const openai = createOpenAIClient();

    const prompt = `You are Sarah, a professional AI interviewer for a ${jobDescription.title} position.
      Previous Questions: ${JSON.stringify(previousQuestions)}
      Previous Answers: ${JSON.stringify(previousAnswers)}
      
      Create a natural response that:
      - If there's a previous answer, briefly acknowledge it with "Thank you" or "I understand"
      - Ask the next relevant question
      - Maintains ${config.technicalDepth} technical depth
      - Uses ${config.tone} tone
      Language: ${config.language === 'es' ? 'Spanish' : 'English'}
      
      Keep the response focused and concise.`;

    const completion = await openai.chat.completions.create({
      model: config.model || "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "You are Sarah, a professional and empathetic AI interviewer. Keep responses natural and concise."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: config.temperature || 0.7,
      max_tokens: config.maxTokens || 500
    });

    const response = completion.choices[0]?.message?.content;
    if (!response) {
      throw new Error('No response received from OpenAI');
    }

    return response.trim();
  } catch (error) {
    console.error('Error generating question:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to generate question: ${error.message}`);
    }
    throw new Error('Failed to generate interview question');
  }
}

export async function analyzeResponse(
  question: string,
  answer: string,
  jobDescription: JobDescription,
  config: InterviewConfig
): Promise<InterviewFeedback> {
  try {
    // Validate inputs
    if (!question?.trim()) {
      throw new Error('Question is required');
    }

    if (!answer?.trim()) {
      throw new Error('Answer is required');
    }

    validateJobDescription(jobDescription);
    validateConfig(config);

    const openai = createOpenAIClient();

    const prompt = `As Sarah, analyze this interview response for a ${jobDescription.title} position:
      Question: ${question}
      Answer: ${answer}
      
      Provide your analysis in the following JSON format:
      {
        "conversationalResponse": "your brief, natural acknowledgment and response",
        "score": <number between 1-100>,
        "feedback": "detailed feedback for records",
        "strengths": ["strength1", "strength2"],
        "improvements": ["improvement1", "improvement2"]
      }
      
      Make sure to:
      1. Keep the conversationalResponse brief and natural
      2. Maintain a ${config.tone} tone
      3. Use appropriate technical depth (${config.technicalDepth})
      4. Respond in ${config.language === 'es' ? 'Spanish' : 'English'}`;

    const completion = await openai.chat.completions.create({
      model: config.model || "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "You are Sarah, a professional and empathetic AI interviewer. Always respond with valid JSON."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: config.temperature || 0.7,
      max_tokens: config.maxTokens || 1000,
      response_format: { type: "json_object" }
    });

    const response = completion.choices[0]?.message?.content;
    if (!response) {
      throw new Error('No response received from OpenAI');
    }

    try {
      const feedback = JSON.parse(response);
      
      // Validate required fields
      if (!feedback.conversationalResponse || typeof feedback.score !== 'number' || 
          !feedback.feedback || !Array.isArray(feedback.strengths) || 
          !Array.isArray(feedback.improvements)) {
        throw new Error('Invalid feedback format: missing required fields');
      }

      // Ensure score is within valid range
      feedback.score = Math.max(0, Math.min(100, feedback.score));

      return {
        ...feedback,
        response: feedback.conversationalResponse
      };
    } catch (error) {
      console.error('Error parsing feedback:', error);
      throw new Error('Invalid feedback format');
    }
  } catch (error) {
    console.error('Error analyzing response:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to analyze response: ${error.message}`);
    }
    throw new Error('Failed to analyze interview response');
  }
}

export async function updateInterviewQuestions(
  existingQuestions: InterviewQuestion[],
  jobDescription: JobDescription,
  config: InterviewConfig
): Promise<InterviewQuestion[]> {
  try {
    // Validate inputs
    validateJobDescription(jobDescription);
    validateConfig(config);

    if (!Array.isArray(existingQuestions)) {
      throw new Error('Existing questions must be an array');
    }

    const openai = createOpenAIClient();

    const prompt = `Review and improve these interview questions for a ${jobDescription.title} position:
      Current Questions: ${JSON.stringify(existingQuestions)}
      Job Description: ${jobDescription.description}
      Requirements: ${JSON.stringify(jobDescription.requirements)}
      Responsibilities: ${JSON.stringify(jobDescription.responsibilities)}
      
      Create an improved version that:
      - Maintains the same number of questions
      - Preserves any custom questions (where isCustom is true)
      - Improves clarity and relevance
      - Uses ${config.technicalDepth} technical depth
      - Uses ${config.tone} tone
      
      Return the questions in this exact JSON format:
      {
        "questions": [
          {
            "question": "question text",
            "type": "technical|behavioral|situational",
            "order": number,
            "isCustom": boolean
          }
        ]
      }
      
      Language: ${config.language === 'es' ? 'Spanish' : 'English'}`;

    const completion = await openai.chat.completions.create({
      model: config.model || "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "You are an expert at improving interview questions while maintaining their original intent."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: config.temperature || 0.7,
      max_tokens: config.maxTokens || 2000,
      response_format: { type: "json_object" }
    });

    const response = completion.choices[0]?.message?.content;
    if (!response) {
      throw new Error('No response received from OpenAI');
    }

    try {
      const parsed = JSON.parse(response);
      if (!Array.isArray(parsed.questions)) {
        throw new Error('Invalid response format');
      }

      // Preserve IDs from existing questions where possible
      return parsed.questions.map((q: any, index: number) => {
        const existingQuestion = existingQuestions[index];
        return {
          id: existingQuestion?.id || crypto.randomUUID(),
          question: q.question.trim(),
          type: q.type,
          order: index + 1,
          isCustom: q.isCustom || false
        };
      });
    } catch (error) {
      console.error('Error parsing OpenAI response:', error);
      throw new Error('Failed to parse updated questions');
    }
  } catch (error) {
    console.error('Error updating questions:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update questions: ${error.message}`);
    }
    throw new Error('Failed to update interview questions');
  }
}